import axios from 'axios';
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

import React, { useState, useEffect, useRef } from 'react';

interface ApiDoc {
  id: number;
  filename: string;
  status: 'processing' | 'completed' | 'error';
  processed_rows: number;
  total_rows: number;
  created_at: string;
}

interface Document {
  id: string;
  filename: string;
  status: 'processing' | 'completed' | 'error';
  processedRows: number;
  totalRows: number;
  createdAt: string;
}

const formatHMS = (secs: number) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${h}h ${m}m ${s}s`;
};

const App: React.FC = () => {
  // Notifications
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    visible: boolean;
  }>({ message: '', type: 'info', visible: false });
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (notification.visible) {
      timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, visible: false }));
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [notification.visible]);
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<{ name: string; email: string } | null>(null);
  const [token, setToken] = useState<string | null>(null);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState<string>('');
  const [loginPassword, setLoginPassword] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  
  // Signup form state
  const [signupName, setSignupName] = useState<string>('');
  const [signupEmail, setSignupEmail] = useState<string>('');
  const [signupPassword, setSignupPassword] = useState<string>('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState<string>('');
  const [signupError, setSignupError] = useState<string>('');
  
  // Dashboard state
  const [activeTab, setActiveTab] = useState<'upload' | 'history'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentStatus, setDocumentStatus] = useState<{
    processedRows: number;
    totalRows: number;
    status: 'processing' | 'completed' | 'error';
    eta: number | null;
  } | null>(null);

  // Dynamic ETA per document (in seconds)
  const [etaByDoc, setEtaByDoc] = useState<Record<string, number>>({});
  const etaStateRef = useRef<Record<string, { lastRows: number; lastTs: number; ema: number | null }>>({});

  // State to hold ETA after upload (in seconds)
  const [uploadETA, setUploadETA] = useState<number | null>(null);

  // Refs
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Check for token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      
      // Fetch user data
      fetchUserData();
    }
  }, []);
  
  // Fetch documents when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchDocuments();
    }
  }, [isAuthenticated]);
  
  // Poll for document status
  useEffect(() => {
    if (selectedDocumentId && isAuthenticated) {
      pollDocumentStatus();
      
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [selectedDocumentId, isAuthenticated]);
  
  // Fetch user data
  const fetchUserData = async () => {
    try {
      const response = await axios.get('/auth/user');
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      handleLogout();
    }
  };

  

  // 3) fetchDocuments as before
  const fetchDocuments = async () => {
    try {
      // this will throw if status !== 2xx
      const response = await axios.get<ApiDoc[]>('/documents');

      const docs: Document[] = response.data.map(d => ({
        id:            d.id.toString(),
        filename:      d.filename,
        status:        d.status,
        processedRows: d.processed_rows,
        totalRows:     d.total_rows,
        createdAt:     d.created_at.replace(/\.\d{3,6}/, ''),
      }));

      setDocuments(docs);
      // Auto-select a processing document to enable dynamic ETA after reload
      if (!selectedDocumentId) {
        const processing = docs.find(d => d.status === 'processing');
        if (processing) {
          setSelectedDocumentId(processing.id);
        }
      }

    } catch (err: any) {
      // is this an AxiosError with a 401?
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        handleLogout();      // clear token, redirect, etc.
      } else {
        // generic failure toast
        console.error('Failed to fetch documents:', err);
        setNotification({
          message: 'Failed to load documents.',
          type:    'error',
          visible: true,
        });
      }
    }
  };
  
  // Cancel a running job
  const handleCancel = async (docId: string) => {
    try {
      await axios.post(`/documents/${docId}/cancel`);
      setNotification({ message: 'Processing cancelled', type: 'info', visible: true });
      fetchDocuments();
    } catch (err) {
      setNotification({ message: 'Failed to cancel', type: 'error', visible: true });
    }
  };

  // Poll document status
  const pollDocumentStatus = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`/documents/${selectedDocumentId}/status`);
        const processedRows = response.data.processedRows;
        const totalRows = response.data.totalRows;
        const status = response.data.status as 'processing' | 'completed' | 'error';
        const avgSecPerRow = response.data.avgSecPerRow ?? response.data.avg_sec_per_row;
        const etaFromServer = response.data.etaSeconds ?? response.data.eta_seconds;

        // Compute dynamic average seconds per row using EMA based on polling deltas
        const nowSec = Date.now() / 1000;
        const prev = etaStateRef.current[selectedDocumentId!];
        let ema: number | null = prev?.ema ?? null;
        if (prev && typeof processedRows === 'number' && processedRows > prev.lastRows) {
          const dRows = processedRows - prev.lastRows;
          const dTime = Math.max(nowSec - prev.lastTs, 0.001);
          const sample = dTime / dRows; // seconds per row
          const alpha = 0.5; // smooth but responsive
          ema = (ema == null) ? sample : (alpha * sample + (1 - alpha) * ema);
        }
        etaStateRef.current[selectedDocumentId!] = {
          lastRows: processedRows,
          lastTs: nowSec,
          ema,
        };

        let etaSeconds: number | null = null;
        if (status === 'processing' && typeof totalRows === 'number' && typeof processedRows === 'number') {
          const rowsLeft = Math.max(totalRows - processedRows, 0);
          if (typeof etaFromServer === 'number') {
            etaSeconds = Math.max(0, Math.ceil(etaFromServer));
          } else if (typeof avgSecPerRow === 'number' && avgSecPerRow > 0) {
            etaSeconds = Math.ceil(rowsLeft * avgSecPerRow);
          } else if (ema != null && ema > 0) {
            etaSeconds = Math.ceil(rowsLeft * ema);
          } else {
            etaSeconds = null;
          }
        } else if (status === 'completed') {
          etaSeconds = 0;
        }

        setDocumentStatus({
          processedRows,
          totalRows,
          status,
          eta: etaSeconds,
        });

        if (etaSeconds != null) {
          setEtaByDoc(prevMap => ({ ...prevMap, [selectedDocumentId!]: etaSeconds! }));
        }
        
        if (response.data.status === 'completed' || response.data.status === 'error') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // Refresh documents list
          fetchDocuments();
        }
      } catch (error) {
        console.error('Failed to fetch document status:', error);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    };
    
    // Fetch immediately
    fetchStatus();
    
    // Then poll every 5 seconds
    pollingIntervalRef.current = setInterval(fetchStatus, 5000);
  };
  
  // Handle login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      // Create proper URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('username', loginEmail); // Must be 'username' field
      formData.append('password', loginPassword);

      const response = await axios.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded' // Crucial header
        }
      });

      // Response format should match FastAPI's OAuth2 spec
      const { access_token: token, user } = response.data;
      
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);
      
      setLoginEmail('');
      setLoginPassword('');
      
      setNotification({ 
        message: 'Successfully logged in', 
        type: 'success', 
        visible: true 
      });
    } catch (error) {
      console.error('Login failed:', error);
      setLoginPassword(''); // Clear password on error
      
      if (axios.isAxiosError(error)) {
        // Handle FastAPI validation errors
        const errorMessage = error.response?.data?.detail?.[0]?.msg || 
                            error.response?.data?.detail ||
                            'Invalid email or password';
        setLoginError(errorMessage);
      } else {
        setLoginError('Login failed. Please try again.');
      }
    }
  };
  
  // Handle signup
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');
    
    // Validate passwords match
    if (signupPassword !== signupConfirmPassword) {
      setSignupError('Passwords do not match');
      return;
    }
    
    try {
      const response = await axios.post('/auth/signup', {
        name: signupName,
        email: signupEmail,
        password: signupPassword
      });
      
      const { token, user } = response.data;
      
      // Store token and set axios default header
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);
      
      // Reset form
      setSignupName('');
      setSignupEmail('');
      setSignupPassword('');
      setSignupConfirmPassword('');
      
      setNotification({ 
        message: 'Account created successfully. Please log in', 
        type: 'success', 
        visible: true 
      });

    } catch (error) {
      console.error('Signup failed:', error);
      setSignupError('Failed to create account. Email may already be in use.');
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    // Reset dashboard state
    setActiveTab('upload');
    setSelectedFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setDocuments([]);
    setSelectedDocumentId(null);
    setDocumentStatus(null);
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  
  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check if file is .xlsx
      if (!file.name.endsWith('.xlsx')) {
        setNotification({ 
          message: 'Only .xlsx files are allowed', 
          type: 'error', 
          visible: true 
        });

        return;
      }
      
      setSelectedFile(file);
      setUploadStatus('idle');
      setUploadProgress(0);
    }
  };
  
  // Handle file drop
  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      
      // Check if file is .xlsx
      if (!file.name.endsWith('.xlsx')) {
        setNotification({ 
          message: 'Only .xlsx files are allowed', 
          type: 'error', 
          visible: true 
        });

        return;
      }
      
      setSelectedFile(file);
      setUploadStatus('idle');
      setUploadProgress(0);
    }
  };
  
  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploadStatus('uploading');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post<{ id: string; total_rows: number }>('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { id, total_rows } = response.data;
      setSelectedDocumentId(id);
      setUploadStatus('success');
      // Show initial ETA based on 10s per row
      setUploadETA((total_rows) * 10);
      fetchDocuments();
    } catch (error) {
      setUploadStatus('error');
      setNotification({ message: 'Failed to upload file', type: 'error', visible: true });
    }
  };
  
  // Handle document download
  const handleDownload = async (documentId: string, type: 'stats' | 'logs') => {
    try {
      const response = await axios.get(`/documents/${documentId}/download/${type}`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${documentId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
      setNotification({ 
        message: 'Failed to download file', 
        type: 'error', 
        visible: true 
      });

    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {notification.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 max-w-md transition-all duration-300 ${
          notification.type === 'success' ? 'bg-green-500 text-white' :
          notification.type === 'error' ? 'bg-red-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          <div className="flex items-center">
            <span className="mr-2">
              {notification.type === 'success' && <i className="fas fa-check-circle"></i>}
              {notification.type === 'error' && <i className="fas fa-exclamation-circle"></i>}
              {notification.type === 'info' && <i className="fas fa-info-circle"></i>}
            </span>
            <p>{notification.message}</p>
            <button 
              className="ml-4 text-white hover:text-gray-200 cursor-pointer rounded-button whitespace-nowrap"
              onClick={() => setNotification(prev => ({ ...prev, visible: false }))}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <i className="fas fa-file-excel text-blue-600 text-2xl mr-2"></i>
              <h1 className="text-xl font-semibold text-gray-900">Excel Processor</h1>
            </div>
            {isAuthenticated && user && (
              <div className="flex items-center">
                <span className="text-gray-700 mr-4">
                  Welcome, {user.name}
                </span>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 cursor-pointer !rounded-button whitespace-nowrap"
                >
                  <i className="fas fa-sign-out-alt mr-2"></i>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!isAuthenticated ? (
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-12rem)]">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden w-full max-w-md">
              <div className="px-6 py-8">
                <div className="mb-6 text-center">
                  <i className="fas fa-lock text-blue-600 text-4xl mb-4"></i>
                  <h2 className="text-2xl font-bold text-gray-900">Authentication</h2>
                  <p className="text-gray-600">Sign in to your account or create a new one</p>
                </div>
                
                <div className="flex border-b">
                  <button
                    className={`px-4 py-2 font-medium text-sm flex-1 text-center ${
                      activeTab === 'upload' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('upload')}
                  >
                    Login
                  </button>
                  <button
                    className={`px-4 py-2 font-medium text-sm flex-1 text-center ${
                      activeTab === 'history' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('history')}
                  >
                    Sign Up
                  </button>
                </div>
                
                {activeTab === 'upload' ? (
                  <form onSubmit={handleLogin} className="mt-6">
                    {loginError && (
                      <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                        {loginError}
                      </div>
                    )}
                    
                    <div className="mb-4">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <i className="fas fa-envelope text-gray-400"></i>
                        </div>
                        <input
                          id="email"
                          type="email"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          required
                          className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <i className="fas fa-lock text-gray-400"></i>
                        </div>
                        <input
                          id="password"
                          type="password"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          required
                          className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer !rounded-button whitespace-nowrap"
                    >
                      Sign in
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleSignup} className="mt-6">
                    {signupError && (
                      <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                        {signupError}
                      </div>
                    )}
                    
                    <div className="mb-4">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <i className="fas fa-user text-gray-400"></i>
                        </div>
                        <input
                          id="name"
                          type="text"
                          value={signupName}
                          onChange={(e) => setSignupName(e.target.value)}
                          required
                          className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <i className="fas fa-envelope text-gray-400"></i>
                        </div>
                        <input
                          id="signup-email"
                          type="email"
                          value={signupEmail}
                          onChange={(e) => setSignupEmail(e.target.value)}
                          required
                          className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <i className="fas fa-lock text-gray-400"></i>
                        </div>
                        <input
                          id="signup-password"
                          type="password"
                          value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          required
                          className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <i className="fas fa-lock text-gray-400"></i>
                        </div>
                        <input
                          id="confirm-password"
                          type="password"
                          value={signupConfirmPassword}
                          onChange={(e) => setSignupConfirmPassword(e.target.value)}
                          required
                          className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer !rounded-button whitespace-nowrap"
                    >
                      Create Account
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
                <p className="text-gray-600">Upload and manage your Excel files</p>
              </div>
              
              <div className="flex border-b mb-6">
                <button
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === 'upload' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                  } cursor-pointer !rounded-button whitespace-nowrap`}
                  onClick={() => setActiveTab('upload')}
                >
                  <i className="fas fa-upload mr-2"></i>
                  Upload File
                </button>
                <button
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === 'history' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                  } cursor-pointer !rounded-button whitespace-nowrap`}
                  onClick={() => setActiveTab('history')}
                >
                  <i className="fas fa-history mr-2"></i>
                  Document History
                </button>
              </div>
              
              {activeTab === 'upload' ? (
                <div>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center ${
                      isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
                    } transition-colors duration-200 cursor-pointer`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".xlsx"
                      className="hidden"
                    />
                    
                    <i className="fas fa-file-excel text-blue-500 text-4xl mb-4"></i>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      {selectedFile ? selectedFile.name : 'Drop your Excel file here or click to browse'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Only .xlsx files are accepted
                    </p>
                    
                    {selectedFile && uploadStatus === 'idle' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpload();
                        }}
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer !rounded-button whitespace-nowrap"
                      >
                        <i className="fas fa-cloud-upload-alt mr-2"></i>
                        Upload File
                      </button>
                    )}
                    
                    {uploadStatus === 'uploading' && (
                      <div className="mt-4">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                          <div 
                            className="bg-blue-600 h-2.5 rounded-full" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Uploading... {uploadProgress}%
                        </p>
                      </div>
                    )}
                    
                    {uploadStatus === 'success' && uploadETA !== null && (
                      <div className="mt-6 p-4 bg-blue-50 text-blue-800 rounded-md">
                        <i className="fas fa-info-circle mr-2"></i>
                        The file has been accepted for processing. Estimated time:&nbsp;
                        <strong>{formatHMS(uploadETA)}</strong>. You’ll receive the results at&nbsp;
                        <strong>{user?.email}</strong>.
                      </div>
                    )}
                    
                    {uploadStatus === 'error' && (
                      <div className="mt-4 text-red-600">
                        <i className="fas fa-exclamation-circle text-xl mr-2"></i>
                        <span>Upload failed</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  {documents.length === 0 ? (
                    <div className="text-center py-12">
                      <i className="fas fa-file-excel text-gray-400 text-4xl mb-4"></i>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">No documents yet</h3>
                      <p className="text-sm text-gray-500">
                        Upload your first Excel file to get started
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Filename
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Processed
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Time Left
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {documents.map((doc) => (
                            <tr key={doc.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <i className="fas fa-file-excel text-blue-500 mr-2"></i>
                                  <span className="text-sm font-medium text-gray-900">{doc.filename}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  doc.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                  doc.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {doc.status === 'processing' && <i className="fas fa-spinner fa-spin mr-1"></i>}
                                  {doc.status === 'completed' && <i className="fas fa-check-circle mr-1"></i>}
                                  {doc.status === 'error' && <i className="fas fa-exclamation-circle mr-1"></i>}
                                  {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {doc.processedRows}/{doc.totalRows}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {doc.status === 'processing'
                                  ? (etaByDoc[doc.id] != null
                                      ? formatHMS(etaByDoc[doc.id])
                                      : formatHMS(Math.max(doc.totalRows - doc.processedRows, 0) * 10))
                                  : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(doc.createdAt).toLocaleString('en-GB', {
                                  day:   '2-digit',
                                  month: '2-digit',
                                  year:  'numeric',
                                  hour:   '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleDownload(doc.id, 'stats')}
                                    disabled={doc.status !== 'completed'}
                                    className={`inline-flex items-center px-2 py-1 border border-transparent text-xs rounded text-white ${
                                      doc.status === 'completed' 
                                        ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer' 
                                        : 'bg-blue-300 cursor-not-allowed'
                                    } !rounded-button whitespace-nowrap`}
                                  >
                                    <i className="fas fa-chart-bar mr-1"></i>
                                    Stats
                                  </button>
                                  <button
                                    onClick={() => handleDownload(doc.id, 'logs')}
                                    disabled={doc.status !== 'completed'}
                                    className={`inline-flex items-center px-2 py-1 border border-transparent text-xs rounded text-white ${
                                      doc.status === 'completed' 
                                        ? 'bg-green-600 hover:bg-green-700 cursor-pointer' 
                                        : 'bg-green-300 cursor-not-allowed'
                                    } !rounded-button whitespace-nowrap`}
                                  >
                                    <i className="fas fa-file-alt mr-1"></i>
                                    Logs
                                  </button>
                                  {doc.status === 'processing' && (
                                    <button
                                      onClick={() => handleCancel(doc.id)}
                                      className="px-2 py-1 bg-red-600 text-white rounded text-xs"
                                    >
                                      Cancel
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} Excel Processor. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-gray-500 cursor-pointer">
                <i className="fab fa-github text-lg"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-gray-500 cursor-pointer">
                <i className="fab fa-twitter text-lg"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-gray-500 cursor-pointer">
                <i className="fab fa-linkedin text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;

